import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINES = [
  { text: "Ihr alter Pool.", cls: "text-[var(--foam)]" },
  { text: "Neue Technik.", cls: "text-[var(--lagoon)] font-accent" },
  { text: "Zweites Leben.", cls: "text-outline" },
];
const ease = [0.16, 1, 0.3, 1];

const MaskLine = ({ children, i, className }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${className}`} initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.2, delay: 0.25 + i * 0.14, ease }}>
      {children}
    </motion.span>
  </span>
);

const TiltFrame = () => {
  const rx = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });
  const ry = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 14);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 14);
  };
  const reset = () => { rx.set(0); ry.set(0); };
  return (
    <div className="[perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div
        data-testid="hero-tilt-frame"
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 24px 24px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 24px 24px)" }}
        transition={{ duration: 1.6, delay: 0.5, ease }}
        className="relative aspect-[4/5] w-full overflow-hidden rounded-t-full rounded-b-3xl spotlight"
      >
        <img src="/images/pooltechnik-box.webp" alt="Pooltechnik-Box mit Steuerung, Filter und Elektrik am Pool" className="h-full w-full object-cover object-[55%_center] scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#04171B] via-transparent to-transparent" />
        <div style={{ transform: "translateZ(60px)" }} className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/15 bg-[rgba(4,23,27,0.6)] p-4 backdrop-blur-md">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--lagoon)]">Status · Pool</p>
          <p className="mt-1 font-display text-lg text-[var(--foam)]">Filter, Heizung, Licht – eine Steuerung.</p>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden pt-28 pb-16 sm:pt-36">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img src="/images/wellen.jpg" alt="" className="h-[120%] w-full object-cover opacity-[0.22]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(60,230,200,0.18),transparent_55%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#04171B]/40 via-[#04171B]/70 to-[#04171B]" />
      </motion.div>

      <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-end gap-12 px-5 sm:px-8 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="lg:col-span-8">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }} className="mb-8 inline-flex items-center gap-3 rounded-full border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--mist)]">
            <span className="h-2 w-2 rounded-full bg-[var(--lagoon)] pulse-dot" /> Modernisierung für Bestandspools
          </motion.p>
          <h1 data-testid="hero-headline" className="font-display font-semibold leading-[0.92] tracking-[-0.035em] text-[13vw] sm:text-[10vw] lg:text-[7.4vw] xl:text-[8.2rem]">
            {LINES.map((l, i) => (
              <MaskLine key={l.text} i={i} className={l.cls}>{l.text}</MaskLine>
            ))}
          </h1>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.9, ease }} className="mt-10 max-w-xl">
            <p className="text-base sm:text-lg leading-relaxed text-[var(--mist)]">
              <span className="text-[var(--foam)]">REVIVO-Smartpool</span> ist das Technik-Upgrade der coolpool company: individuelle Poolsteuerung, integrierte Elektrik und kristallklare Wasseraufbereitung – nachgerüstet in Ihren bestehenden Pool.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button data-testid="hero-primary-cta" onClick={() => scrollToId("pool-check")} className="btn-primary justify-center">
                In 2 Minuten Pool-Check machen <ArrowRight className="h-4 w-4" />
              </button>
              <button data-testid="hero-secondary-cta" onClick={() => scrollToId("vorher-nachher")} className="btn-ghost justify-center">
                Vorher / Nachher ansehen
              </button>
            </div>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-4 mx-auto w-full max-w-sm lg:max-w-none">
          <TiltFrame />
        </div>
      </div>

      <button data-testid="hero-scroll-indicator" onClick={() => scrollToId("problem")} className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--mist)] sm:flex">
        Scrollen <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
      </button>
    </section>
  );
};
